import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { LegalTable as Table } from '../components/LegalLayout';
import { COMPANY } from '../lib/company';
import { useSEO } from '../hooks/useSEO';

const { emails } = COMPANY;

const sections = [
  {
    id: 'who-we-are',
    title: 'Who we are',
    body: (
      <>
        <p>
          This Privacy Policy explains how <strong>{COMPANY.legalName}</strong>, trading as {COMPANY.tradingName} (&ldquo;IP9&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;), collects and uses personal data when you visit{' '}
          <a href={COMPANY.website}>ip9.io</a>, use the customer dashboard at dashboard.ip9.io, or buy and use our residential and mobile proxy services (together, the &ldquo;Services&rdquo;).
        </p>
        <p>
          {COMPANY.legalName} is a company registered in {COMPANY.jurisdiction} under company number {COMPANY.companyNumber}, with its registered office at {COMPANY.address}. We are the{' '}
          <strong>controller</strong> of the personal data described in this policy for the purposes of the UK General Data Protection Regulation (&ldquo;UK GDPR&rdquo;), the Data Protection Act 2018 and, where it applies to
          you, the EU General Data Protection Regulation 2016/679 (&ldquo;EU GDPR&rdquo;).
        </p>
        <p>
          For any privacy question or to exercise your rights, contact us at <a href={`mailto:${emails.privacy}`}>{emails.privacy}</a> or by post at our registered office, marked &ldquo;Data Protection&rdquo;.
        </p>
      </>
    ),
  },
  {
    id: 'scope',
    title: 'Scope of this policy',
    body: (
      <>
        <p>This policy covers personal data about website visitors, prospective customers, customers and their authorised users, and people who contact us.</p>
        <p>
          It does not cover the content of traffic you route through our proxy network or the websites you access with it. When you use the Services to collect or process data, you decide what is collected and why, so
          you act as an independent controller for that data and are responsible for complying with data protection law (see our <Link to="/acceptable-use-policy">Acceptable Use Policy</Link>).
        </p>
      </>
    ),
  },
  {
    id: 'data-we-collect',
    title: 'Personal data we collect',
    body: (
      <>
        <Table
          head={['Category', 'Examples', 'Source']}
          rows={[
            ['Account data', 'Name, email address, password (stored as a salted hash), company name, phone number if you provide it', 'You'],
            ['Billing and transaction data', 'Billing address, VAT or tax number, order history, invoices, payment status. Card details are handled by our payment providers; we do not store full card numbers', 'You and our payment providers'],
            ['Service usage metadata', 'Proxy usernames, whitelisted IP addresses, bandwidth used, connection timestamps, the IP address you connect from, and the hostnames you connect to (used for billing, abuse prevention and network health)', 'Generated when you use the Services'],
            ['Communications', 'Support requests, emails, Telegram messages, call notes and feedback', 'You'],
            ['Website and device data', 'IP address, browser and device type, pages viewed, referring page. Analytics and advertising data is collected only if you accept those cookies', 'Your browser'],
            ['Compliance data', 'Information needed to verify identity or the intended use of the Services when we investigate abuse, fraud or sanctions risk', 'You, public sources, fraud and sanctions screening services'],
          ]}
        />
        <p>
          <strong>We do not inspect, record or store the content of traffic that passes through our proxy network.</strong> We process only the connection metadata described above.
        </p>
        <p>
          Giving us account and billing data is necessary to enter into and perform our contract with you. If you do not provide it, we cannot provide the Services. Everything else is optional.
        </p>
      </>
    ),
  },
  {
    id: 'purposes',
    title: 'How we use personal data and our lawful bases',
    body: (
      <>
        <Table
          head={['Purpose', 'Lawful basis (UK/EU GDPR Art. 6)']}
          rows={[
            ['Creating and managing your account, authenticating proxy access and delivering the Services', 'Performance of a contract (Art. 6(1)(b))'],
            ['Processing payments, issuing invoices, keeping accounting and tax records', 'Contract (Art. 6(1)(b)) and legal obligation (Art. 6(1)(c))'],
            ['Customer support and service messages (e.g. usage alerts, security notices, changes to terms)', 'Contract (Art. 6(1)(b)) and our legitimate interest in running the Services (Art. 6(1)(f))'],
            ['Preventing fraud, abuse, unlawful use and attacks on our network; enforcing our terms', 'Legitimate interests in protecting our network, customers and the public (Art. 6(1)(f)); legal obligation where applicable'],
            ['Sending existing customers news about similar IP9 services', 'Legitimate interests (Art. 6(1)(f)) and the “soft opt-in” under PECR. You can opt out at any time'],
            ['Sending marketing to anyone else', 'Consent (Art. 6(1)(a))'],
            ['Website analytics through Google Analytics and PostHog, including pseudonymous session replays with form inputs masked', 'Consent (Art. 6(1)(a)), given through our cookie banner'],
            ['Measuring our Facebook and Instagram adverts and showing IP9 adverts to past website visitors, using the Meta Pixel', 'Consent (Art. 6(1)(a)), given through our cookie banner'],
            ['Improving the Services and network performance using aggregated usage data', 'Legitimate interests (Art. 6(1)(f))'],
            ['Complying with law, responding to lawful requests from authorities, establishing or defending legal claims', 'Legal obligation (Art. 6(1)(c)) and legitimate interests (Art. 6(1)(f))'],
          ]}
        />
        <p>
          Where we rely on legitimate interests, we have balanced them against your rights and you can ask us for details. We do not make decisions that produce legal or similarly significant effects about you based
          solely on automated processing. We use automated tools to detect abuse, but a person reviews any decision to permanently close an account.
        </p>
      </>
    ),
  },
  {
    id: 'cookies',
    title: 'Cookies, analytics and advertising',
    body: (
      <p>
        We use strictly necessary storage to run the website and, only with your consent, analytics cookies from Google Analytics and PostHog and marketing cookies from the Meta Pixel. You can change your choice at any time using &ldquo;Cookie settings&rdquo; in the
        footer. Full details are in our <Link to="/cookie-policy">Cookie Policy</Link>.
      </p>
    ),
  },
  {
    id: 'sharing',
    title: 'Who we share personal data with',
    body: (
      <>
        <p>We do not sell personal data. We share it only with:</p>
        <ul>
          <li>
            <strong>Service providers acting as our processors</strong>, under written contracts that require them to protect it and use it only on our instructions: cloud hosting and databases (Railway Corporation), content
            delivery, DNS and security (Cloudflare, Inc.), payment processing, email delivery, customer support tools, and, with your consent, analytics (Google Ireland Limited / Google LLC and PostHog, Inc.).
          </li>
          <li>
            <strong>Meta Platforms Ireland Limited</strong>, only if you accept marketing cookies. The Meta Pixel sends Meta the pages you visit on our website with your browser, device and IP address details, so we can measure
            our adverts and show IP9 adverts on Facebook and Instagram. We and Meta are joint controllers for collecting and transmitting this data, under Meta&rsquo;s{' '}
            <a href="https://www.facebook.com/legal/controller_addendum" target="_blank" rel="noopener noreferrer">Controller Addendum</a>; Meta is the controller for its own later processing, described in the{' '}
            <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer">Meta Privacy Policy</a>. You can exercise your rights over that later processing directly with Meta.
          </li>
          <li>
            <strong>Upstream network partners</strong> who supply residential and mobile connectivity, limited to the technical data needed to route traffic and handle abuse reports.
          </li>
          <li>
            <strong>Professional advisers</strong> such as lawyers, accountants and auditors, under a duty of confidentiality.
          </li>
          <li>
            <strong>Authorities and law enforcement</strong>, when we are legally required to disclose data, or when disclosure is necessary to prevent crime or protect the rights, property or safety of IP9, our customers or others.
            We assess each request and disclose only what is required.
          </li>
          <li>
            <strong>A buyer or successor</strong> if we are involved in a merger, acquisition or sale of assets, subject to this policy.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'transfers',
    title: 'International transfers',
    body: (
      <>
        <p>
          Some of our service providers process data outside the United Kingdom and the European Economic Area, mainly in the United States. When we transfer personal data internationally, we make sure it is protected by
          one of the following:
        </p>
        <ul>
          <li>an adequacy decision or UK adequacy regulations (including the EU-U.S. Data Privacy Framework and its UK Extension, for certified recipients);</li>
          <li>the European Commission&rsquo;s Standard Contractual Clauses, together with the UK International Data Transfer Addendum or the UK International Data Transfer Agreement; or</li>
          <li>another safeguard or exception permitted by Chapter V of the UK GDPR or EU GDPR.</li>
        </ul>
        <p>
          You can request a copy of the relevant safeguards from <a href={`mailto:${emails.privacy}`}>{emails.privacy}</a>.
        </p>
      </>
    ),
  },
  {
    id: 'retention',
    title: 'How long we keep personal data',
    body: (
      <>
        <Table
          head={['Data', 'Retention period']}
          rows={[
            ['Account data', 'For as long as your account is open, then up to 90 days after closure, unless we need it longer for a dispute or legal claim'],
            ['Service usage logs', '30 days, then deleted or aggregated so they no longer identify you'],
            ['Billing, invoices and accounting records', '6 years after the end of the financial year they relate to, as required by UK company and tax law'],
            ['Support communications', 'Up to 2 years after the request is closed'],
            ['Abuse and fraud investigation records', 'For as long as necessary for the investigation and any related legal proceedings, normally no more than 6 years'],
            ['Analytics data (only with consent)', 'According to the retention settings of each tool, no longer than 14 months'],
            ['Meta Pixel cookies (only with consent)', '90 days in your browser; Meta keeps the data it receives under its own retention policy'],
            ['Your cookie choice', '12 months, after which we ask again'],
          ]}
        />
      </>
    ),
  },
  {
    id: 'your-rights',
    title: 'Your rights',
    body: (
      <>
        <p>Under the UK GDPR and EU GDPR you have the right to:</p>
        <ul>
          <li><strong>access</strong> the personal data we hold about you and receive a copy;</li>
          <li><strong>rectify</strong> inaccurate or incomplete data;</li>
          <li><strong>erase</strong> your data where there is no longer a reason for us to keep it;</li>
          <li><strong>restrict</strong> how we use your data in certain circumstances;</li>
          <li><strong>data portability</strong>: receive data you gave us in a structured, machine-readable format, or have it sent to another provider;</li>
          <li><strong>object</strong> to processing based on legitimate interests, and to direct marketing at any time (an absolute right);</li>
          <li><strong>withdraw consent</strong> at any time, without affecting processing that took place before; and</li>
          <li>not be subject to decisions based solely on automated processing that significantly affect you.</li>
        </ul>
        <p>
          To exercise a right, email <a href={`mailto:${emails.privacy}`}>{emails.privacy}</a>. We will reply within one month. We may extend this by two further months for complex requests, and we will tell you if we do. We
          may need to verify your identity first. Exercising your rights is free unless a request is manifestly unfounded or excessive.
        </p>
        <p>
          <strong>Complaints.</strong> If you are unhappy with how we handle your data, please contact us first so we can try to resolve it. You also have the right to complain to a supervisory authority: in the UK, the
          Information Commissioner&rsquo;s Office (<a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer">ico.org.uk</a>, 0303 123 1113); in the EEA, the data protection authority of the
          country where you live or work, or where the alleged breach took place (see the{' '}
          <a href="https://www.edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener noreferrer">list of EEA authorities</a>).
        </p>
      </>
    ),
  },
  {
    id: 'eea-representative',
    title: 'Visitors and customers in the EEA',
    body: (
      <p>
        {COMPANY.legalName} is established in the United Kingdom. If you are in the European Economic Area, you can exercise all your rights by contacting us directly at{' '}
        <a href={`mailto:${emails.privacy}`}>{emails.privacy}</a>. Where Article 27 EU GDPR requires it, the contact details of our representative in the EU are available from the same address.
      </p>
    ),
  },
  {
    id: 'security',
    title: 'How we protect personal data',
    body: (
      <p>
        We use technical and organisational measures appropriate to the risk, including encryption in transit (TLS), encrypted storage of credentials, role-based access controls with multi-factor authentication for staff,
        network monitoring, and regular reviews of our providers. No system is completely secure. If a personal data breach is likely to result in a high risk to you, we will tell you without undue delay.
      </p>
    ),
  },
  {
    id: 'children',
    title: 'Children',
    body: <p>The Services are intended for adults and businesses. We do not knowingly collect personal data from anyone under 18. If you believe a child has given us personal data, contact us and we will delete it.</p>,
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    body: (
      <p>
        We may update this policy when our practices or the law change. We will post the new version on this page with a new &ldquo;Last updated&rdquo; date and, for material changes, tell customers by email or in the dashboard
        before the change takes effect.
      </p>
    ),
  },
  {
    id: 'contact',
    title: 'Contact us',
    body: (
      <p>
        Privacy: <a href={`mailto:${emails.privacy}`}>{emails.privacy}</a> · General support: <a href={`mailto:${emails.support}`}>{emails.support}</a> · Post: {COMPANY.legalName}, {COMPANY.address}.
      </p>
    ),
  },
];

const PrivacyPolicyPage = () => {
  useSEO('privacyPolicy');
  return <LegalLayout title="Privacy Policy" sections={sections} />;
};

export default PrivacyPolicyPage;
